import { BadgeCellRenderer, type TypeBadgeCellRendererProps, type TypeTableColumn } from '@thefarmersfront/kitchen';

import { formatNumber } from '~/utils/common';
import { formatDate } from '~/utils/date';

import {
  DATE_FORMAT,
  RETURN_DISPOSAL_PROCESS_METHOD,
  RETURN_DISPOSAL_PROCESS_STATUS,
  RETURN_DISPOSAL_PROCESS_STATUS_BADGE_COLOR,
  type TypeReturnDisposalProcessMethod,
  type TypeReturnDisposalProcessStatus,
} from '~/constants';

import type { TypeReturnDisposalIssueItemApi } from '~/types/receive.types';

/** 처리상태 코드 → Badge 옵션 맵 (constants 두 객체를 합성) */
export const PROCESS_STATUS_COLOR_MAP = (
  Object.keys(RETURN_DISPOSAL_PROCESS_STATUS) as TypeReturnDisposalProcessStatus[]
).reduce<NonNullable<TypeBadgeCellRendererProps<TypeReturnDisposalProcessStatus>['colorMap']>>(
  (acc, key) => ({
    ...acc,
    [key]: {
      label: RETURN_DISPOSAL_PROCESS_STATUS[key],
      color: RETURN_DISPOSAL_PROCESS_STATUS_BADGE_COLOR[key],
      variant: 'light',
      status: true,
    },
  }),
  {}
);

/**
 * 소비기한/제조일자 표시값 조립
 *
 * @note 소비기한(expirationDate) 우선, 없으면 제조일자(manufactureDate)를 라벨과 함께 노출한다.
 *       (예: `2026.06.30 (소비기한)`) 둘 다 없으면 '-'.
 */
export const formatExpirationOrManufacture = ({
  expirationDate,
  manufactureDate,
}: TypeReturnDisposalIssueItemApi): string => {
  const expiration = formatDate(expirationDate, DATE_FORMAT.DATE);
  if (expiration) return `${expiration} (소비기한)`;

  const manufacture = formatDate(manufactureDate, DATE_FORMAT.DATE);
  if (manufacture) return `${manufacture} (제조일자)`;

  return '-';
};

/**
 * [입고관리 > 회송/폐기 관리] 처리신청 모달 - 상품정보 미리보기 컬럼 정의 (회수 기준, 읽기 전용)
 *
 * @note 중량·이슈발생센터는 회수 모달에서 미노출. 컬럼 폭 합이 컨테이너를 넘으면 가로 스크롤.
 */
const getReturnDisposalIssueItemsColumns = (): TypeTableColumn<TypeReturnDisposalIssueItemApi>[] => [
  {
    accessorKey: 'purchaseOrderCode',
    header: '발주코드',
    cell: (info) => info.getValue<string>() || '-',
    meta: { columnFit: true },
  },
  {
    accessorKey: 'goodsCode',
    header: '상품코드',
    cell: (info) => info.getValue<string>() || '-',
    meta: { columnFit: true },
  },
  {
    accessorKey: 'goodsName',
    header: '상품명',
    minSize: 240,
    cell: (info) => info.getValue<string>() || '-',
    meta: { flex: true },
  },
  {
    id: 'expirationOrManufacture',
    header: '소비기한/제조일자',
    cell: (info) => formatExpirationOrManufacture(info.row.original),
    meta: { columnFit: true },
  },
  {
    accessorKey: 'issueType',
    header: '이슈상세',
    cell: (info) => info.getValue<string>() || '-',
    meta: { columnFit: true },
  },
  {
    accessorKey: 'issueQty',
    header: '이슈수량',
    cell: (info) => {
      const value = info.getValue<number | null>();
      return value || value === 0 ? formatNumber(value) : '-';
    },
    meta: { columnFit: true, cellStyle: { textAlign: 'right' } },
  },
  {
    accessorKey: 'processMethod',
    header: '처리방법',
    cell: (info) => {
      const value = info.getValue<TypeReturnDisposalProcessMethod>();
      return RETURN_DISPOSAL_PROCESS_METHOD[value] ?? '-';
    },
    meta: { columnFit: true },
  },
  {
    accessorKey: 'processStatus',
    header: '처리상태',
    cell: (info) => {
      const value = info.getValue<TypeReturnDisposalProcessStatus>();
      return value ? <BadgeCellRenderer value={value} colorMap={PROCESS_STATUS_COLOR_MAP} /> : '-';
    },
    meta: { columnFit: true },
  },
];

export default getReturnDisposalIssueItemsColumns;
